import type { OverviewBusyHour, OverviewResponse } from "../../api/overview-types";

/** Pure helpers of the home reports (OWN-164 / OWN-319): what is shown, and how raw data becomes chart input. */

/** Sparse `busiestHours` -> a 7 (Sunday first) x 24 grid, ignoring out-of-range or non-positive cells. */
export function buildHeatGrid(cells: readonly OverviewBusyHour[]): {
  readonly grid: number[][];
  readonly max: number;
  readonly total: number;
  readonly peak: OverviewBusyHour | null;
} {
  const grid = Array.from({ length: 7 }, () => Array.from({ length: 24 }, () => 0));
  let max = 0;
  let total = 0;
  let peak: OverviewBusyHour | null = null;
  for (const c of cells) {
    if (!Number.isInteger(c.weekday) || c.weekday < 0 || c.weekday > 6) continue;
    if (!Number.isInteger(c.hour) || c.hour < 0 || c.hour > 23 || !(c.count > 0)) continue;
    const row = grid[c.weekday]!;
    row[c.hour] = (row[c.hour] ?? 0) + c.count;
    total += c.count;
    if ((row[c.hour] ?? 0) > max) {
      max = row[c.hour] ?? 0;
      peak = { weekday: c.weekday, hour: c.hour, count: max };
    }
  }
  return { grid, max, total, peak };
}

export interface VisibleSections {
  readonly kpis: boolean;
  readonly conversationsChart: boolean;
  readonly salesChart: boolean;
  readonly busyHours: boolean;
  readonly team: boolean;
  readonly topProducts: boolean;
  readonly topLabels: boolean;
  readonly channels: boolean;
}

const sum = (xs: readonly number[]) => xs.reduce((a, b) => a + b, 0);

/** Which sections have something to render. A section the API omitted (or left empty) never shows. */
export function visibleSections(d: OverviewResponse): VisibleSections {
  const charts = d.charts;
  return {
    kpis: Boolean(
      d.conversations ??
      d.sales ??
      d.bookings ??
      d.broadcasts ??
      d.wallet ??
      d.contacts ??
      d.csat ??
      d.abandonedCarts,
    ),
    conversationsChart: Boolean(
      charts?.conversationsPerDay?.some((x) => sum(Object.values(x.byChannel)) > 0),
    ),
    salesChart: Boolean(charts?.salesPerDay?.some((x) => Number(x.totalMinor) > 0)),
    busyHours: buildHeatGrid(charts?.busiestHours ?? []).total > 0,
    team: (d.team?.length ?? 0) > 0,
    topProducts: (d.topProducts?.length ?? 0) > 0,
    topLabels: (d.topLabels?.length ?? 0) > 0,
    channels: (d.channels?.length ?? 0) > 0,
  };
}

export const hasAnySection = (v: VisibleSections): boolean => Object.values(v).some(Boolean);
